import axios from 'axios';
import { useLocaleStore } from '../store/locale.store';
import { shouldRedirectOn401, isSessionRevoked, isTokenExpired } from './authRoutes';
import { buildLoginUrl } from '../utils/returnTo';
import { tenantCodeFromHost } from '../utils/host-tenant';
import { toInnerPath, toOuterUrl } from '../utils/tenant-host-history';

/** /login conservando la pantalla actual para volver a ella tras iniciar sesión. */
function loginUrlWithNext(): string {
  try {
    const { pathname, search } = window.location;
    // En un subdominio de empresa la URL del navegador no lleva /<empresa>: se trabaja con la
    // ruta interna (/<empresa>/...) y al final se vuelve a la forma del navegador.
    const code = tenantCodeFromHost();
    const inner = code ? toInnerPath(pathname, code) : pathname;
    const segs = inner.split('/').filter(Boolean);
    const tenant = segs.length >= 2 ? decodeURIComponent(segs[0]) : code;
    const url = buildLoginUrl(inner, search, tenant);
    return code ? toOuterUrl(url, code) : url;
  } catch {
    return '/login';
  }
}

// ---------------------------------------------------------------------------
// Axios client — Agrovid Dashboard
// ---------------------------------------------------------------------------
// Fix (2026-06-08):
//  - 401 interceptor now also calls useAuthStore.logout() so the Zustand store
//    stays in sync before the page redirect.  We import getState() to avoid
//    a React hook violation (this file is not a component).
// ---------------------------------------------------------------------------

const BASE_URL = import.meta.env.VITE_API_URL ?? '';

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
});

// ── Request interceptor: attach JWT + inject tenantId in URL ────────────────
// R-23: se lee de sessionStorage (migrado desde localStorage)
// Multi-tenant: URLs /api/<resource> → /api/:tenantId/<resource> automáticamente.
// Las rutas que ya tienen tenantId (/api/auth/*, /api/super-admin/*) no se tocan.
// /api/tenant/:tenantId/config es lookup público de branding (pre-login, NO AUTH
// en router.ts) — no debe reescribirse con el tenantId de la sesión.
// C-6: /api/permissions/* y /api/users/search viven SIN tenant en router.ts —
// reescribirlas a /api/:tenantId/... producía 404 y dejaba PermissionsMatrixPage
// completamente inoperativa. (Ojo: /api/users/me sí es tenant-scoped, por eso
// solo se exenta users/search, no users completo.)
// invitations: solo las rutas públicas por token (GET /api/invitations/:token y
// .../accept). POST /api/invitations (crear) SÍ es tenant-scoped en el backend:
// /api/:tenantId/invitations — exentarlo daba 404 al generar el link.
export const TENANT_EXEMPT = /^\/api\/(auth|super-admin|demo|shared|invitations\/[^/?]+|version|tenant|permissions|users\/search)(\/|$|\?)/;

apiClient.interceptors.request.use((config) => {
  try {
    const token = sessionStorage.getItem('agrovid_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;

    // Attach Accept-Language header for localized error messages
    const locale = useLocaleStore.getState().locale;
    if (locale) config.headers['Accept-Language'] = locale;

    // Inject tenantId into /api/<resource> → /api/:tenantId/<resource>
    const url = config.url ?? '';
    if (url.startsWith('/api/') && !TENANT_EXEMPT.test(url)) {
      const raw = sessionStorage.getItem('agrovid_user');
      const tenantId: string | undefined = raw ? JSON.parse(raw)?.tenantId : undefined;
      if (tenantId) {
        // Replace /api/ prefix with /api/:tenantId/ — but only if tenantId not already there
        const withoutPrefix = url.slice('/api/'.length);
        // If URL already starts with the tenantId segment, don't double-inject
        if (!withoutPrefix.startsWith(`${tenantId}/`)) {
          config.url = `/api/${tenantId}/${withoutPrefix}`;
        }
      }
    }
  } catch { /* private mode — proceed without token */ }
  return config;
});

// ── Response interceptor: handle 401 ────────────────────────────────────────
// Only redirect to /login if there is genuinely no session token.
// If a token exists but a specific endpoint returns 401 (e.g. a backend bug or
// a missing permission on a non-auth route), we reject the promise without
// nuking the session — otherwise a freshly-logged-in user gets kicked out.
apiClient.interceptors.response.use(
  (res) => res,
  (error) => {
    // Inactive account — redirect to dedicated page regardless of token presence
    if (error.response?.status === 403 && error.response?.data?.error === 'ACCOUNT_INACTIVE') {
      window.location.replace('/account-inactive');
      return Promise.reject(error);
    }

    // Empresa desactivada con sesión ya emitida: se cierra sesión y se vuelve al login.
    // Un cambio de empresa (impersonate) fallido nunca debe tocar la sesión: el error lo muestra quien llamó.
    const isImpersonate = /\/api\/super-admin\/tenants\/[^/]+\/impersonate/.test(error.config?.url ?? '');
    if (isImpersonate) return Promise.reject(error);

    if (error.response?.status === 403 && error.response?.data?.code === 'TENANT_INACTIVE') {
      import('../store/auth.store').then(({ useAuthStore }) => {
        useAuthStore.getState().logout();
        window.location.replace(loginUrlWithNext());
      });
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      let hasToken = false;
      let expired = false;
      try {
        const token = sessionStorage.getItem('agrovid_token');
        hasToken = !!token;
        expired = isTokenExpired(token);
      } catch { /* ignore */ }

      // Sesión revocada o token vencido: el token ya no sirve para nada, así que se cierra sesión y se vuelve
      // al login en lugar de quedar en "Acceso denegado".
      if (hasToken && (expired || isSessionRevoked(error.response?.data))) {
        import('../store/auth.store').then(({ useAuthStore }) => {
          useAuthStore.getState().logout();
          window.location.replace(loginUrlWithNext());
        });
        return Promise.reject(error);
      }

      // Un 401 en rutas de autenticación (credenciales inválidas) se muestra en
      // la pantalla de login sin recargarla.
      if (shouldRedirectOn401(error.config?.url, hasToken)) {
        // Truly unauthenticated — clear store and redirect
        import('../store/auth.store').then(({ useAuthStore }) => {
          useAuthStore.getState().logout();
          window.location.replace(loginUrlWithNext());
        });
      }
      // If hasToken: backend returned 401 on a specific endpoint (bug/permissions).
      // Reject promise so the calling hook can handle it (isError) without logging out.
    }
    return Promise.reject(error);
  },
);
